import axios, { AxiosError } from 'axios'
import { useAuthStore } from '../stores/auth'

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const state = useAuthStore.getState()
    
    // Use API Key auth if available (preferred for demo/testing)
    if (state.apiKey && state.tenantId) {
      config.headers['x-api-key'] = state.apiKey
      config.headers['x-tenant-id'] = state.tenantId
    }
    // Fall back to JWT Bearer token
    else if (state.token) {
      config.headers.Authorization = `Bearer ${state.token}`
    }
    
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Unauthorized - logout
      useAuthStore.getState().logout()
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// API functions
export const swarmApi = {
  getAll: (params?: { status?: string; page?: number; per_page?: number }) =>
    api.get('/swarms', { params }),
  
  getById: (id: string) =>
    api.get(`/swarms/${id}`),
  
  create: (data: {
    name: string
    description?: string
    task: string
    config?: Partial<{
      maxAgents: number
      model: string
      temperature: number
      tools: string[]
      enableFactChecking: boolean
      enableSelfImprovement: boolean
    }>
  }) => api.post('/swarms', data),
  
  delete: (id: string) =>
    api.delete(`/swarms/${id}`),
  
  getAgents: (swarmId: string) =>
    api.get(`/swarms/${swarmId}/agents`),
  
  getAgent: (swarmId: string, agentId: string) =>
    api.get(`/swarms/${swarmId}/agents/${agentId}`),
}

export const tenantApi = {
  getCurrent: () =>
    api.get('/tenant'),
  
  getStats: (params?: { days?: number }) =>
    api.get('/tenant/stats', { params }),
}

export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
}

export const artifactApi = {
  download: async (storageKey: string, artifactName: string) => {
    // Use full API URL to bypass Pages and go directly to Worker
    const apiUrl = `${import.meta.env.VITE_API_URL || ''}/artifacts/${storageKey}`
    console.log('Downloading artifact from:', apiUrl)
    
    const state = useAuthStore.getState()
    const headers: Record<string, string> = {}
    
    if (state.apiKey && state.tenantId) {
      headers['x-api-key'] = state.apiKey
      headers['x-tenant-id'] = state.tenantId
    } else if (state.token) {
      headers['Authorization'] = `Bearer ${state.token}`
    }
    
    const response = await fetch(apiUrl, { headers })
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error')
      throw new Error(`Download failed: ${response.status} - ${errorText}`)
    }
    
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = artifactName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }
}

export default api
